// One consent-aware owner for Meta. GTM custom tags are blocked in index.html.
import { getConsent, hasServiceConsent } from './consent';
import { pushConsentUpdate } from './analytics';
import { META_PIXEL_ID, GOOGLE_ADS_ID, GTM_ID, setMetaReady, googleCommand, type MetaQueue } from './tracking-runtime';
import { installTrackingTransportGuard } from './tracking-transport';

let metaInitialized = false;
let metaScriptLoaded = false;
let metaAllowed = false;
let metaLoading: Promise<void> | undefined;
let gadsInitialized = false;
let gadsLoading: Promise<void> | undefined;
let gtmStarted = false;
let listening = false;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { script.remove(); reject(new Error('Tracking script unavailable')); };
    document.head.appendChild(script);
  });
}

function ensureMetaQueue(): MetaQueue {
  if (window.fbq) return window.fbq;
  const n = function (...args: unknown[]) {
    // Also gates SDK-generated events and calls by other application code.
    if (args[0] !== 'consent' && !hasServiceConsent('metaPixel')) return;
    if (n.callMethod) n.callMethod.apply(n, args);
    else n.queue.push(args);
  } as MetaQueue;
  n.queue = [];
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  window.fbq = n;
  window._fbq = n;
  return n;
}

export async function initMetaPixel(): Promise<void> {
  if (!hasServiceConsent('metaPixel')) return;
  if (metaLoading) return metaLoading;
  if (metaInitialized) {
    if (!metaAllowed) {
      window.fbq?.('consent', 'grant');
      metaAllowed = true;
      setMetaReady(true);
      window.fbq?.('track', 'PageView');
    }
    return;
  }
  const fbq = ensureMetaQueue();
  metaLoading = (async () => {
    try {
      if (!metaScriptLoaded) {
        await loadScript('https://connect.facebook.net/en_US/fbevents.js');
        metaScriptLoaded = true;
      }
      // The visitor may have withdrawn permission while the script loaded.
      if (!hasServiceConsent('metaPixel')) { fbq.queue.length = 0; fbq('consent', 'revoke'); return; }
      fbq('consent', 'grant');
      fbq('init', META_PIXEL_ID);
      metaInitialized = true;
      metaAllowed = true;
      setMetaReady(true);
      fbq('track', 'PageView');
    } catch {
      console.warn('[Tracking] Failed to load Meta Pixel');
    } finally { metaLoading = undefined; }
  })();
  return metaLoading;
}

export async function initGoogleAds(): Promise<void> {
  if (gadsInitialized || !hasServiceConsent('googleAds')) return;
  if (gadsLoading) return gadsLoading;
  gadsLoading = (async () => {
    try {
      await loadScript(`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`);
      if (!hasServiceConsent('googleAds')) return;
      googleCommand('set', 'developer_id.dZjgwMW', true);
      googleCommand('js', new Date());
      googleCommand('config', GOOGLE_ADS_ID);
      gadsInitialized = true;
      googleCommand('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/bkWZCJmU4ZAcENCi2IlD`, value: 1.0, currency: 'EUR',
      });
    } catch { console.warn('[Tracking] Failed to load Google Ads'); }
    finally { gadsLoading = undefined; }
  })();
  return gadsLoading;
}

export function trackGoogleAdsConversion(): void {
  if (!gadsInitialized || !hasServiceConsent('googleAds')) return;
  googleCommand('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/6VhJCJWSwJAcENCi2IlD`, value: 1.0, currency: 'EUR',
  });
}

function syncTracking(): void {
  const consent = getConsent();
  // GA's explicit opt-out stops automatic/enhanced measurement after withdrawal,
  // unlike denied consent mode alone, which can still send cookieless pings.
  (window as unknown as Record<string, unknown>)['ga-disable-G-GXSKG6P081'] = !hasServiceConsent('ga4');
  pushConsentUpdate(consent?.statistics === true, consent?.marketing === true);
  if (hasServiceConsent('ga4') && !gtmStarted) {
    gtmStarted = true;
    const w = window as Window & { dataLayer?: unknown[] };
    w.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    void loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`).catch(() => { gtmStarted = false; });
  }
  if (hasServiceConsent('metaPixel')) void initMetaPixel();
  else {
    setMetaReady(false);
    metaAllowed = false;
    if (window.fbq) {
      window.fbq.queue.length = 0;
      window.fbq('consent', 'revoke');
    }
    // Remove first-party Meta cookies on withdrawal, including parent domains.
    for (const name of ['_fbp', '_fbc']) {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      const parts = location.hostname.split('.');
      for (let i = 0; i < parts.length - 1; i++) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parts.slice(i).join('.')}`;
      }
    }
  }
  if (hasServiceConsent('googleAds')) void initGoogleAds();
}

export function initTracking(): void {
  installTrackingTransportGuard();
  if (!listening) {
    listening = true;
    window.addEventListener('h2h-consent-change', syncTracking);
    window.addEventListener('storage', (event) => {
      if (event.key === 'h2h_cookie_consent' || event.key === null) syncTracking();
    });
  }
  syncTracking();
}
